import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_MS = 60000;  // 15s → 60s: 4× fewer events with no visible UX change (engagement resolution still 1 min)
const FLUSH_MS = 20000;      // 10s → 20s to stay well under the 100/min rate budget
const MAX_BUFFER = 40;       // hard cap — force flush when buffer hits this
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const SCROLL_MILESTONES = [25, 50, 75, 100];

// ─── Visitor UUID ───
function getVisitorId() {
  let id = localStorage.getItem("_ae_vid");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("_ae_vid", id);
  }
  return id;
}

// ─── Session management ───
function getSessionId() {
  const stored = sessionStorage.getItem("_ae_sid");
  const ts = sessionStorage.getItem("_ae_sts");
  if (stored && ts && Date.now() - Number(ts) < SESSION_TIMEOUT_MS) {
    sessionStorage.setItem("_ae_sts", String(Date.now()));
    return stored;
  }
  const sid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem("_ae_sid", sid);
  sessionStorage.setItem("_ae_sts", String(Date.now()));
  return sid;
}

// ─── UTM parsing ───
function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || sessionStorage.getItem("_ae_utm_src") || "",
    utm_medium: params.get("utm_medium") || sessionStorage.getItem("_ae_utm_med") || "",
    utm_campaign: params.get("utm_campaign") || sessionStorage.getItem("_ae_utm_cmp") || "",
  };
}

function persistUtm() {
  const params = new URLSearchParams(window.location.search);
  if (params.get("utm_source")) sessionStorage.setItem("_ae_utm_src", params.get("utm_source"));
  if (params.get("utm_medium")) sessionStorage.setItem("_ae_utm_med", params.get("utm_medium"));
  if (params.get("utm_campaign")) sessionStorage.setItem("_ae_utm_cmp", params.get("utm_campaign"));
}

// ─── Referrer parsing ───
function getReferrerDomain() {
  try {
    const ref = document.referrer;
    if (!ref) return "";
    const url = new URL(ref);
    if (url.hostname === window.location.hostname) return "";
    return url.hostname.replace("www.", "");
  } catch {
    return "";
  }
}

// ─── Bot detection ───
function isBot() {
  if (navigator.webdriver) return true;
  const ua = navigator.userAgent.toLowerCase();
  const bots = ["bot", "spider", "crawler", "headless", "lighthouse", "pagespeed"];
  return bots.some((b) => ua.includes(b));
}

// ─── Browser info ───
function getBrowserInfo() {
  const ua = navigator.userAgent;
  let browser = "Unknown";
  if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome/") && !ua.includes("Edg/")) browser = "Chrome";
  else if (ua.includes("Safari/") && !ua.includes("Chrome/")) browser = "Safari";
  else if (ua.includes("Opera/") || ua.includes("OPR/")) browser = "Opera";

  let os = "Unknown";
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  const isMobile = /Mobi|Android|iPhone|iPad/.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)/.test(ua);
  const device_type = isTablet ? "tablet" : (isMobile ? "mobile" : "desktop");

  return { browser, os, device_type };
}

// ═══════ SINGLETON STATE (module-scoped) ═══════
// Prior to Feb 2026 this state lived inside the hook. When the hook was
// mounted on multiple pages (App + LandingPage + VerifiedPage), each mount
// spawned its own buffer + 10s flush interval — a single navigation could
// therefore trigger 3 independent POSTs to /analytics/collect. Moving state
// out of the hook makes ALL mounts share ONE buffer and ONE flusher,
// dropping the rate-limit budget hit by ~3x.
const _visitorId = typeof window !== "undefined" ? getVisitorId() : "";
const _browserInfo = typeof window !== "undefined" ? getBrowserInfo() : { browser: "", os: "", device_type: "" };
const _referrerDomain = typeof window !== "undefined" ? getReferrerDomain() : "";
const _eventBuffer = [];
let _flushTimer = null;
let _heartbeatTimer = null;
let _lastActivity = Date.now();
let _globalListenersAttached = false;

function _flushNow() {
  if (_eventBuffer.length === 0) return;
  const events = _eventBuffer.splice(0, _eventBuffer.length);
  // Pull the current logged-in user_id from localStorage so the backend
  // can tag the analytics session. sendBeacon can't set an Authorization
  // header, so we include the id in the payload body instead. The backend
  // still prefers the JWT when a request comes with an Authorization
  // header (e.g. from an authenticated axios call) and only falls back
  // to payload.user_id when the header is absent.
  let user_id = null;
  try {
    const rawUser = localStorage.getItem("eros_user");
    if (rawUser) user_id = JSON.parse(rawUser).user_id || null;
  } catch {
    user_id = null;
  }
  const payload = {
    events,
    visitor_id: _visitorId,
    session_id: getSessionId(),
    ua: navigator.userAgent,
    user_id,
  };
  if (navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    navigator.sendBeacon(`${API}/api/analytics/collect`, blob);
  } else {
    axios.post(`${API}/api/analytics/collect`, payload).catch(() => {});
  }
}

function _pushEvent(evt) {
  _eventBuffer.push(evt);
  if (_eventBuffer.length >= MAX_BUFFER) _flushNow();
}

function _ensureGlobalTimers() {
  if (_globalListenersAttached || typeof window === "undefined") return;
  _globalListenersAttached = true;

  const onActivity = () => { _lastActivity = Date.now(); };
  window.addEventListener("mousemove", onActivity, { passive: true });
  window.addEventListener("keydown", onActivity, { passive: true });
  window.addEventListener("scroll", onActivity, { passive: true });
  window.addEventListener("touchstart", onActivity, { passive: true });

  _heartbeatTimer = setInterval(() => {
    if (Date.now() - _lastActivity < 60000) {
      _pushEvent(_createBaseEvent("heartbeat", { active_seconds: Math.round(HEARTBEAT_MS / 1000) }));
    }
  }, HEARTBEAT_MS);

  _flushTimer = setInterval(_flushNow, FLUSH_MS);

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") _flushNow();
  });
  window.addEventListener("pagehide", _flushNow);
}

function _createBaseEvent(type, extra = {}) {
  const utm = getUtmParams();
  return {
    visitor_id: _visitorId,
    session_id: getSessionId(),
    type,
    path: window.location.pathname,
    title: document.title,
    referrer: document.referrer,
    referrer_domain: _referrerDomain,
    active_seconds: 0,
    scroll_depth: 0,
    click_target: "",
    click_text: "",
    click_tag: "",
    event_name: "",
    event_label: "",
    browser: _browserInfo.browser,
    os: _browserInfo.os,
    device_type: _browserInfo.device_type,
    screen_w: window.screen.width,
    screen_h: window.screen.height,
    is_authenticated: !!localStorage.getItem("eros_access_token"),
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    timestamp: Date.now() / 1000,
    ...extra,
  };
}

// ═══════ THE HOOK ═══════
export function useAnalytics() {
  const location = useLocation();
  const scrollMilestonesHit = useRef(new Set());

  const queueEvent = useCallback((type, extra = {}) => {
    if (isBot()) return;
    _pushEvent(_createBaseEvent(type, extra));
  }, []);

  // ─── Track page view ───
  useEffect(() => {
    if (isBot()) return;
    scrollMilestonesHit.current = new Set();
    persistUtm();
    _ensureGlobalTimers();
    queueEvent("pageview");
  }, [location.pathname, queueEvent]);

  // ─── Scroll depth tracking (per-mount, cheap) ───
  useEffect(() => {
    if (isBot()) return;
    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);
      for (const milestone of SCROLL_MILESTONES) {
        if (pct >= milestone && !scrollMilestonesHit.current.has(milestone)) {
          scrollMilestonesHit.current.add(milestone);
          queueEvent("scroll", { scroll_depth: milestone });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname, queueEvent]);

  // ─── Click tracking (buttons + links) ───
  useEffect(() => {
    if (isBot()) return;
    const onClick = (e) => {
      const target = e.target.closest("a, button, [data-testid], [role='button']");
      if (!target) return;
      const text = (target.textContent || "").trim().slice(0, 100);
      const tag = target.tagName.toLowerCase();
      const testId = target.getAttribute("data-testid") || "";
      const href = target.getAttribute("href") || "";
      queueEvent("click", {
        click_target: testId || href || `${tag}.${target.className?.split?.(" ")?.[0] || ""}`,
        click_text: text,
        click_tag: tag,
      });
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [queueEvent]);

  // ─── Signup form tracking ───
  useEffect(() => {
    if (isBot()) return;
    let formFocused = false;
    const onFocus = (e) => {
      const form = e.target.closest("[data-testid='signup-form']");
      if (form && !formFocused) {
        formFocused = true;
        queueEvent("form_interact", { event_name: "signup_form_focus", event_label: "first_focus" });
      }
    };
    document.addEventListener("focusin", onFocus, { capture: true });
    return () => document.removeEventListener("focusin", onFocus, { capture: true });
  }, [location.pathname, queueEvent]);

  const trackConversion = useCallback((name, label = "") => {
    queueEvent("conversion", { event_name: name, event_label: label });
  }, [queueEvent]);

  const trackFormAbandon = useCallback((name, label = "") => {
    queueEvent("conversion", { event_name: name, event_label: label });
  }, [queueEvent]);

  return { trackConversion, trackFormAbandon };
}
